import { currencyFormatter } from "../../../../shared/components/ui/atoms";

const customCurrencyFormatter = (value: any, digits = 0) => (
  `$${currencyFormatter(value, digits).replace(/\$/g, '')}`
)

/**
 * Instances of this class format all the values collected
 * before they are used in the loan resume and in the term sheet.
 */
export class TermSheet {
  address: string;
  loanType: string;
  propertyType: string;
  creditScore: string;
  investmentExperience: string;
  price: number;
  currentLoanBalance: number;
  cashoutAmount: number;
  rehabBudget: number | null;
  arv: number | null;
  monthlyRent: number | null;
  annualTaxes: number | null;
  annualInsurance: number | null;
  annualHOA: number | null;
  dscr: number | null;
  ltv: number;
  ltc: number | null;
  rate: number;
  totalLoanAmount: number;
  buydownPoints: number;

  /* Class Utils */
  static isRentalLoan(loanType: string) {
    return loanType === "Rental";
  }

  static constructForBridgeLoan(input: any) {
    return new TermSheet(
      input.address,
      "Bridge",
      input.propertyType,
      input.creditScore,
      input.investmentExperience,
      input.price,
      input.currentLoanBalance,
      input.cashoutAmount,
      null,
      null,
      null,
      null,
      null,
      null,
      null,
      input.ltv,
      null,
      input.rate,
      input.totalLoanAmount,
      input.buydownPoints
    )
  }

  static constructForFixAndFlipLoan(input: any) {
    return new TermSheet(
      input.address,
      "FixAndFlip",
      input.propertyType,
      input.creditScore,
      input.investmentExperience,
      input.price,
      input.currentLoanBalance,
      input.cashoutAmount,
      input.rehabBudget,
      input.arv,
      null,
      null,
      null,
      null,
      null,
      input.ltv,
      input.ltc,
      input.rate,
      input.totalLoanAmount,
      input.buydownPoints
    )
  }

  static constructForRentalLoan(input: any) {
    return new TermSheet(
      input.address,
      "Rental",
      input.propertyType,
      input.creditScore,
      input.investmentExperience || "",
      input.price,
      input.currentLoanBalance,
      input.cashoutAmount,
      null,
      null,
      input.monthlyRent,
      input.annualTaxes,
      input.annualInsurance,
      input.annualHOA,
      input.dscr,
      input.ltv,
      null,
      input.rate,
      input.totalLoanAmount,
      input.buydownPoints
    )
  }

  /* Class Constants */
  HOLDBACK_PERCENTAGE = 0.1;
  ORIGINATION_PERCENTAGE = 0.0174;
  SERVICE_FEE = 995;
  EST_LEGAL_COSTS = 1250;

  /* Instance */
  constructor(
    address: string,
    loanType: string,
    propertyType: string,
    creditScore: string,
    investmentExperience: string,
    price: number,
    currentLoanBalance: number,
    cashoutAmount: number,
    rehabBudget: number | null,
    arv: number | null,
    monthlyRent: number | null,
    annualTaxes: number | null,
    annualInsurance: number | null,
    annualHOA: number | null,
    dscr: number | null,
    ltv: number,
    ltc: number | null,
    rate: number,
    totalLoanAmount: number,
    buydownPoints: number
  ) {
    this.address = address;
    this.loanType = loanType;
    this.propertyType = propertyType;
    this.creditScore = creditScore;
    this.investmentExperience = investmentExperience;
    this.price = price;
    this.currentLoanBalance = currentLoanBalance;
    this.cashoutAmount = cashoutAmount;
    this.rehabBudget = rehabBudget;
    this.monthlyRent = monthlyRent;
    this.annualTaxes = annualTaxes;
    this.annualInsurance = annualInsurance;
    this.annualHOA = annualHOA;
    this.dscr = dscr;
    this.arv = arv;
    this.ltv = ltv;
    this.ltc = ltc;
    this.rate = rate;
    this.totalLoanAmount = Number(totalLoanAmount);
    this.buydownPoints = buydownPoints ?? 0;
  }

  /* Instance Utils */
  get isRentalLoan() {
    return (this.constructor as typeof TermSheet).isRentalLoan(this.loanType)
  }

  get isRefinance() {
    return this.currentLoanBalance >= 0
  }

  /* Section General Info */
  get totalLoanAmountFormatted() {
    return customCurrencyFormatter(this.totalLoanAmount, 0)
  }
  /**
   * For refinance loans
   * 
   * If this value is negative, it means it's a cashout loan
   * as we need money to close the loan
   */
  get closingCashAmount() {
    const feesAmount = this.buydownFeeAmount + this.originationFeeAmount + this.SERVICE_FEE + this.EST_LEGAL_COSTS + this.rentalFeeAmount;
    if (this.isRefinance) {
      return (this.currentLoanBalance + feesAmount) - (this.totalLoanAmount - (this.rehabBudget || 0))
    } else {
      return this.downPaymentAmount + feesAmount
    }
  }

  get closingCash() {
    return customCurrencyFormatter(this.closingCashAmount, 0)
  }

  get originationFeeAmount() {
    return this.totalLoanAmount * this.ORIGINATION_PERCENTAGE
  }

  get originationFee() {
    return customCurrencyFormatter(this.originationFeeAmount, 0);
  }

  get originationFeePercentage() {
    return `${(this.ORIGINATION_PERCENTAGE * 100).toFixed(2)}%`;
  }

  get serviceFee() {
    const amount = this.SERVICE_FEE
    return customCurrencyFormatter(amount, 0)
  }

  get buydownFeeAmount() {
    return this.totalLoanAmount * (this.buydownPoints / 100)
  }

  get buydownFee() {
    return customCurrencyFormatter(this.buydownFeeAmount, 0)
  }

  get rentalFeeAmount() {
    let rentalFee = 0
    if (this.isRentalLoan) {
      rentalFee = ((this.annualInsurance || 0) / 2) + ((this.annualTaxes || 0) / 2)
    }
    return rentalFee
  }

  get rentalFee() {
    return customCurrencyFormatter(this.rentalFeeAmount, 0)
  }

  get downPaymentAmount() {
    if (this.isRefinance) {
      return 0
    } else {
      return this.price - (this.totalLoanAmount - (this.rehabBudget || 0))
    }
  }

  get downPayment() {
    return customCurrencyFormatter(this.downPaymentAmount, 0)
  }

  get proRatedInterest() {
    const perDiemRate = (this.rate) / 365
    const perDiemInterest = perDiemRate * this.totalLoanAmount
    return customCurrencyFormatter(perDiemInterest, 0)
  }

  get thirdPartyCosts() {
    return "Contact closing agent"
  }

  get legalCosts() {
    return customCurrencyFormatter(this.EST_LEGAL_COSTS, 0)
  }

  /* Section Additional Details */
  get loanTypeDisplay() {
    if (this.isRentalLoan) {
      return "Rental"
    } else {
      if (this.loanType === "Bridge") {
        return "Bridge"
      } else {
        return "Fix & Flip"
      }
    }
  }

  get amortization() {
    if (this.isRentalLoan) {
      return "30 year"
    } else {
      return "Interest Only"
    }
  }

  get lien() {
    return "1st position"
  }

  get asIsValue() {
    return customCurrencyFormatter(this.price)
  }

  get estimatedARV() {
    if (!this.arv) {
      return "N/A"
    }
    return customCurrencyFormatter(this.arv)
  }

  get holdbackAmount() {
    const amount = (this.currentLoanBalance || 0) * this.HOLDBACK_PERCENTAGE
    return customCurrencyFormatter(amount, 0)
  }

  /* PDF Elements */
  get advanceAmount() {
    return customCurrencyFormatter(this.totalLoanAmount - (this.rehabBudget || 0))
  }

  get ltvDisplay() {
    return `${(this.ltv * 100).toFixed(2)}%`
  }

  get constructionReserve() {
    if (!this.rehabBudget) {
      return "N/A"
    }

    return customCurrencyFormatter(this.rehabBudget)
  }

  get creditScoreDisplay() {
    return this.creditScore
  }

  get investmentExperienceDisplay() {
    return this.investmentExperience
  }

  get propertyTypeDisplay() {
    return this.propertyType
  }

  get loanPurpose() {
    if (this.isRefinance) {
      if (this.closingCashAmount < 0) {
        return "Cashout Refi"
      } else {
        return "NoCashout Refi"
      }
    } else {
      return "Purchase"
    }
  }

  get ltcDisplay() {
    if (!this.ltc) {
      return "N/A"
    }

    return `${Number(this.ltc * 100).toFixed(1)}%`
  }

  get interestRate() {
    return `${Number(this.rate).toFixed(2)}%`
  }

  get term() {
    return this.isRentalLoan ? "30 Years" : "12 Months"
  }

  get buydownPercentage() {
    return `${this.buydownPoints}%`
  }

  get _buydownPoints() {
    return this.buydownPoints
  }

  /* Rental */
  get stimatedMonthlyRent() {
    const amount = this.monthlyRent
    return customCurrencyFormatter(amount)
  }

  get annualExpenses() {
    const amount = (this.annualTaxes || 0) + (this.annualInsurance || 0) + (this.annualHOA || 0)
    return customCurrencyFormatter(amount)
  }

  get dscrDisplay() {
    return this.dscr
  }
}

/**
 * Calculates the base rate based on investment experience
 * @param {string} investmentExperience - The investment experience value
 * @returns {number} - The base rate percentage
 */
const getBaseRateByInvestmentExperience = (investmentExperience: string) => {
  switch (investmentExperience) {
    case 'None':
      return 10.75;
    case '1-2 properties':
      return 10.75;
    case '3-5 properties':
      return 10.25;
    case '6+ properties':
      return 10.00;
    default:
      return 10.75; // Default fallback
  }
};

/**
 * Calculates the opportunity value based on the loan type:
 * - For DSCR loans (Rental): ALL FEES + (RATE DIFFERENCE × LOAN AMOUNT)
 * - For Fix & Flip and Bridge loans: ALL FEES + (BOND PRICE DIFFERENCE × LOAN AMOUNT)
 * 
 * @param {TermSheet} termSheetData - The term sheet data object (instance of TermSheet class)
 * @param {Object} selectedOption - The selected option object
 * @returns {number} - Opportunity value
 */
export const calculateOpportunityValue = (termSheetData: TermSheet, selectedOption: any) => {
  // Use the getter methods to get the calculated values
  const originationFeeAmount = termSheetData.originationFeeAmount || 0;
  const buydownFeeAmount = termSheetData.buydownFeeAmount || 0;
  const serviceFee = 995; // This is a constant from the class
  const totalLoanAmount = termSheetData.totalLoanAmount || 0;
  const rate = termSheetData.rate || 0;

  // Calculate all fees
  const allFees = originationFeeAmount + buydownFeeAmount + serviceFee;

  let opportunityValue;

  if (termSheetData.isRentalLoan) {
    // DSCR loans (Rental) - use rate difference calculation
    // Base rate for comparison based on investment experience
    const bondPriceDifference = Math.abs(selectedOption.bondPrice - 100);

    // Calculate the percentage of the difference
    const differencePercentage = bondPriceDifference / 100;

    // Calculate the opportunity value
    opportunityValue = allFees + (differencePercentage * totalLoanAmount);

  } else {
    // Fix & Flip and Bridge loans - use bond price calculation
    // Calculate the difference between bond price (rate) and 100, then get absolute value

    const baseRate = getBaseRateByInvestmentExperience(termSheetData.investmentExperience);

    // Calculate the rate difference
    const rateDifference = baseRate - rate;

    // Calculate the opportunity value from rate difference
    const rateDifferenceValue = (rateDifference / 100) * totalLoanAmount;

    // Calculate the total opportunity value
    opportunityValue = originationFeeAmount + rateDifferenceValue;
  }

  return Number(opportunityValue.toFixed(2));
};